"use client"

import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import FlowList from "./components/FlowList";
import FlowDetail from "./components/FlowDetail";
import { Toaster } from "../../components/ui/toaster";
import { connect } from "react-redux";
import { getFlows } from "../../../ApiCalls/mantisFlowsManangment";
import { selectAuthState } from "../../../store/selectors/authSelectors";
import { ArrowLeft } from "lucide-react";
import { Button } from "../../components/ui/button";

function FlowManagement(props) {
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flows, setFlows] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const initialFlowIdFromUrl = searchParams.get("flowId");

  // Función para mapear los estados de la API a los estados de la UI
  const mapApiStatusToUi = (apiStatus) => {
    switch (apiStatus) {
      case "PREPARING":
        return "processing";
      case "READY":
        return "active";
      case "ERROR":
        return "error";
      default:
        return "processing";
    }
  };

  useEffect(() => {
    const fetchFlows = async () => {
      try {
        const response = await getFlows(props.mantisBusiness._id);

        if (response.success) {
          // Adaptamos los datos para que coincidan con el formato esperado
          const adaptedFlows = response.data.map((flow) => ({
            id: flow.id,
            name: flow.name,
            status: mapApiStatusToUi(flow.status),
            description: "Flujo de trabajo automatizado",
            documentCount: 0,
            createdAt: new Date(flow.created_at).toLocaleDateString(),
          }));

          setFlows(adaptedFlows);
        } else {
          setError("No se pudieron cargar los flujos");
        }
      } catch (err) {
        console.error("Error al cargar los flujos:", err);
        setError("Error al cargar los flujos");
      } finally {
        setLoading(false);
      }
    };

    fetchFlows();
  }, [props.mantisBusiness._id]);

  useEffect(() => {
    const flowId = searchParams.get("flowId");
    if (flowId && flows.length > 0) {
      const flowFromQuery = flows.find((flow) => flow.id + "" === flowId); // Assuming flow objects have an 'id' property
      if (flowFromQuery) {
        setSelectedFlow(flowFromQuery);
      } else {
        // Optional: Handle case where flowId from URL is not found after flows load
        // For now, it will fall through to showing FlowList
      }
    }
  }, [searchParams, flows]);

  const handleFlowSelect = (flow) => {
    setSelectedFlow(flow);
    // Update the URL with the selected flow ID
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set("flowId", flow.id);
    navigate(`${location.pathname}?${newSearchParams.toString()}`, {
      replace: true,
    });
  };

  const handleBack = () => {
    setSelectedFlow(null);
    // Remove the flowId from the URL
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.delete("flowId");
    navigate(`${location.pathname}?${newSearchParams.toString()}`, {
      replace: true,
    });
  };

  if (selectedFlow) {
    return (
      <div>
        <FlowDetail flow={selectedFlow} onBack={handleBack} />
        <Toaster />
      </div>
    );
  }

  if (initialFlowIdFromUrl && loading) {
    // If a flowId is in the URL and we are still loading the main flow list
    // show a targeted loader instead of the FlowList's skeleton.
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="space-y-6"
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className="mr-2"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <p className="text-slate-500 dark:text-slate-400">
                Cargando resultados...
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded shadow-sm border border-slate-200 dark:border-slate-700 p-8 text-center">
          <div className="animate-pulse flex flex-col items-center">
            <div className="h-8 w-8 bg-slate-200 dark:bg-slate-700 rounded-full mb-4"></div>
            <div className="h-4 w-48 bg-slate-200 dark:bg-slate-700 rounded mb-2.5"></div>
            <div className="h-3 w-32 bg-slate-200 dark:bg-slate-700 rounded"></div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Default to showing the FlowList
  // This will be shown if:
  // - No initialFlowIdFromUrl and loading=true (FlowList shows its own skeleton)
  // - No initialFlowIdFromUrl and loading=false (FlowList shows actual flows or error)
  // - initialFlowIdFromUrl was present, but loading=false and selectedFlow is still null (e.g. flow not found)
  return (
    <div>
      <h1 className="text-3xl font-bold mb-6 text-slate-800 dark:text-slate-100">
        Flow Management
      </h1>
      <FlowList
        flows={flows}
        onFlowSelect={handleFlowSelect}
        loading={loading}
        error={error}
      />
      <Toaster />
    </div>
  );
}

export default connect(selectAuthState)(FlowManagement);